import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SuperAdminSidebar } from '../../components/Sidebar';
import api from '../../utils/api';

const STATUS_LABELS = {
  pending: 'Ausstehend', info_required: 'Info erforderlich',
  trial_active: 'Testphase aktiv', trial_expired: 'Test abgelaufen',
  active: 'Aktiv', expired: 'Abgelaufen', blocked: 'Gesperrt',
};

const COUNTRIES = { CH: 'Schweiz', DE: 'Deutschland', AT: 'Österreich' };
const ROLES = [
  { value: 'imam', label: 'Imam' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'coordinator', label: 'Koordinator' },
  { value: 'other', label: 'Sonstiges' },
];

export default function MosqueDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');

  // Edit state
  const [editInfo, setEditInfo] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [editLogin, setEditLogin] = useState(false);
  const [editPlan, setEditPlan] = useState(false);

  // Form states
  const [infoForm, setInfoForm] = useState({});
  const [contactForm, setContactForm] = useState({});
  const [loginForm, setLoginForm] = useState({ newPassword: '', confirmPassword: '' });
  const [planForm, setPlanForm] = useState({ plan: 'free', months: 1, isTrial: false, amount: '' });

  // Verify
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState('');
  const [analytics, setAnalytics] = useState(null);

  const notify = (text, type = 'success') => { setMsg(text); setMsgType(type); };

  const reload = useCallback(() => {
    api.get(`/superadmin/mosques/${id}`)
      .then(r => {
        setData(r.data);
        const m = r.data.mosque;
        setInfoForm({ name: m.name, city: m.city, address: m.address || '', website: m.website || '', countryCode: m.country_code });
        setContactForm({ contactName: m.contact_name, contactRole: m.contact_role, contactEmail: m.contact_email, contactPhone: m.contact_phone || '' });
        setPlanForm({ plan: m.plan || 'free', months: 1, isTrial: m.is_trial });
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    api.get(`/superadmin/mosques/${id}/analytics`).then(r => setAnalytics(r.data)).catch(() => {});
  }, [id]);

  const doExport = async () => {
    try {
      const res = await api.get(`/superadmin/mosques/${id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data?.mosque?.name || 'moschee').replace(/[^\w-]+/g, '_')}-export.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { notify('Export fehlgeschlagen', 'error'); }
  };

  const doDelete = async () => {
    const name = data?.mosque?.name || '';
    if (!window.confirm(`Moschee "${name}" und ALLE Daten (Quizze, Sessions, Teilnehmer, Rechnungen …) unwiderruflich löschen?\n\nTipp: Exportieren Sie vorher die Daten.`)) return;
    if (!window.confirm('Wirklich ENDGÜLTIG löschen? Dies kann nicht rückgängig gemacht werden.')) return;
    setSaving('delete');
    try {
      await api.delete(`/superadmin/mosques/${id}`);
      window.location.href = '/superadmin/mosques';
    } catch (e) {
      notify('Löschen fehlgeschlagen: ' + (e.response?.data?.error || e.message), 'error');
      setSaving('');
    }
  };

  const doVerify = async (act) => {
    setSaving('verify');
    try {
      await api.patch(`/superadmin/mosques/${id}/verify`, { action: act, reason });
      notify(`Aktion "${act}" erfolgreich ausgeführt`);
      reload();
    } catch (err) {
      notify('Fehler: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSaving(''); }
  };

  const saveInfo = async (e) => {
    e.preventDefault();
    setSaving('info');
    try {
      await api.put(`/superadmin/mosques/${id}`, {
        mosqueName: infoForm.name,
        city: infoForm.city,
        address: infoForm.address || undefined,
        website: infoForm.website || undefined,
        countryCode: infoForm.countryCode,
      });
      notify('Moschee-Informationen gespeichert');
      setEditInfo(false);
      reload();
    } catch (err) {
      notify('Fehler: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSaving(''); }
  };

  const saveContact = async (e) => {
    e.preventDefault();
    setSaving('contact');
    try {
      await api.put(`/superadmin/mosques/${id}`, {
        contactName: contactForm.contactName,
        contactRole: contactForm.contactRole,
        contactEmail: contactForm.contactEmail,
        contactPhone: contactForm.contactPhone || undefined,
      });
      notify('Kontaktdaten gespeichert');
      setEditContact(false);
      reload();
    } catch (err) {
      notify('Fehler: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSaving(''); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (loginForm.newPassword !== loginForm.confirmPassword) {
      notify('Passwörter stimmen nicht überein', 'error'); return;
    }
    setSaving('login');
    try {
      await api.patch(`/superadmin/mosques/${id}/reset-password`, {
        newPassword: loginForm.newPassword,
      });
      notify('Passwort erfolgreich geändert');
      setEditLogin(false);
      setLoginForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      notify('Fehler: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSaving(''); }
  };

  const savePlan = async () => {
    setSaving('plan');
    try {
      const r = await api.patch(`/superadmin/mosques/${id}/plan`, {
        plan: planForm.plan,
        months: planForm.months,
        amount: planForm.amount ? parseFloat(planForm.amount) : undefined,
      });
      const invoiceMsg = r.data.invoice ? ` — Rechnung ${r.data.invoice.invoice_number} erstellt` : '';
      notify(`Plan auf "${planForm.plan}" fur ${planForm.months} Monat(e) aktualisiert${invoiceMsg}`);
      setEditPlan(false);
      reload();
    } catch (err) {
      notify('Fehler: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSaving(''); }
  };

  const changeStatus = async (newStatus) => {
    setSaving('status');
    try {
      await api.patch(`/superadmin/mosques/${id}/status`, { status: newStatus });
      notify(`Status auf "${STATUS_LABELS[newStatus]}" geändert`);
      reload();
    } catch (err) {
      notify('Fehler: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSaving(''); }
  };

  const blockUnblock = async (block) => {
    setSaving('block');
    try {
      if (block) {
        const r = window.prompt('Begründung (optional):');
        if (r === null) { setSaving(''); return; }
        await api.patch(`/superadmin/mosques/${id}/block`, { reason: r });
        notify('Konto gesperrt');
      } else {
        await doVerify('approve');
        return;
      }
      reload();
    } catch (err) {
      notify('Fehler: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSaving(''); }
  };

  if (loading) return <div className="spinner-wrap"><div className="spinner" /></div>;
  const m = data?.mosque;

  return (
    <div style={{ display: 'flex' }}>
      <SuperAdminSidebar />
      <main className="main-content">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <Link to="/superadmin/mosques" className="btn btn-ghost btn-sm">← Zurück</Link>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 26 }}>{m?.name}</h1>
            <p style={{ color: 'var(--text3)', fontSize: 13 }}>
              {m?.city}, {COUNTRIES[m?.country_code] || m?.country_code} · {m?.login_email}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span className={`badge ${
              m?.status === 'active' ? 'badge-green' :
              m?.status === 'blocked' ? 'badge-red' : 'badge-orange'
            }`} style={{ fontSize: 13, padding: '6px 14px' }}>
              {STATUS_LABELS[m?.status] || m?.status}
            </span>
          </div>
        </div>

        {msg && (
          <div className={`alert ${msgType === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 20 }}>
            {msg}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 860 }}>

          {/* Verification (for pending mosques) */}
          {m?.status === 'pending' && (
            <div className="card" style={{ borderLeft: '4px solid var(--accent)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Verifikation</h3>
              <div className="form-group">
                <label className="form-label">Begründung (optional)</label>
                <input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="z.B. Ablehnung oder Info-Anfrage..." />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={() => doVerify('approve')} disabled={saving === 'verify'}>Genehmigen</button>
                <button className="btn btn-ghost" onClick={() => doVerify('request_info')} disabled={saving === 'verify'}>Info anfordern</button>
                <button className="btn btn-danger" onClick={() => doVerify('reject')} disabled={saving === 'verify'}>Ablehnen</button>
              </div>
            </div>
          )}

          {/* Analytics / Nutzung */}
          <div className="card">
            <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
              Statistik &amp; Nutzung
            </h3>
            {!analytics ? (
              <p style={{ color: 'var(--text3)', fontSize: 13 }}>Wird geladen…</p>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
                  {[
                    { label: 'Quizze', value: analytics.totals?.total_quizzes },
                    { label: 'Sessions gesamt', value: analytics.totals?.total_sessions },
                    { label: 'Abgeschlossen', value: analytics.totals?.finished_sessions },
                    { label: 'Letzte 30 Tage', value: analytics.totals?.sessions_30d },
                    { label: 'Teilnehmer gesamt', value: analytics.totals?.total_participants },
                  ].map((s, i) => (
                    <div key={i} style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)' }}>{s.value ?? 0}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 14 }}>
                  Letzte Aktivität: {analytics.totals?.last_session_at ? new Date(analytics.totals.last_session_at).toLocaleDateString('de-CH') : '—'}
                </div>
                {analytics.monthly && analytics.monthly.length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Sessions pro Monat (6 Mte)</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 70 }}>
                      {(() => { const max = Math.max(...analytics.monthly.map(x => x.sessions), 1); return analytics.monthly.map((x, i) => (
                        <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                          <div title={String(x.sessions)} style={{ height: Math.round((x.sessions / max) * 56) + 4, background: 'var(--accent)', borderRadius: 4 }} />
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{x.month.slice(2)}</div>
                        </div>
                      )); })()}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Moschee Info */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editInfo ? 20 : 0 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Moschee-Informationen
              </h3>
              <button className="btn btn-sm btn-ghost" onClick={() => setEditInfo(p => !p)}>
                {editInfo ? 'Abbrechen' : 'Bearbeiten'}
              </button>
            </div>
            {editInfo ? (
              <form onSubmit={saveInfo}>
                <div className="grid-2" style={{ gap: 16 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Name *</label>
                    <input className="form-input" value={infoForm.name || ''} onChange={e => setInfoForm(p => ({ ...p, name: e.target.value }))} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Stadt *</label>
                    <input className="form-input" value={infoForm.city || ''} onChange={e => setInfoForm(p => ({ ...p, city: e.target.value }))} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Land</label>
                    <select className="form-select" value={infoForm.countryCode || 'CH'} onChange={e => setInfoForm(p => ({ ...p, countryCode: e.target.value }))}>
                      {Object.entries(COUNTRIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Adresse</label>
                    <input className="form-input" value={infoForm.address || ''} onChange={e => setInfoForm(p => ({ ...p, address: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ margin: 0, gridColumn: '1/-1' }}>
                    <label className="form-label">Website</label>
                    <input className="form-input" value={infoForm.website || ''} onChange={e => setInfoForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." />
                  </div>
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" type="submit" disabled={saving === 'info'}>
                    {saving === 'info' ? 'Speichern...' : 'Speichern'}
                  </button>
                  <button className="btn btn-ghost" type="button" onClick={() => setEditInfo(false)}>Abbrechen</button>
                </div>
              </form>
            ) : (
              <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                <InfoRow label="Name" value={m?.name} />
                <InfoRow label="Stadt" value={m?.city} />
                <InfoRow label="Land" value={COUNTRIES[m?.country_code] || m?.country_code} />
                <InfoRow label="Adresse" value={m?.address || '—'} />
                <InfoRow label="Website" value={m?.website || '—'} />
                <InfoRow label="ID" value={<span style={{ fontFamily: 'monospace', fontSize: 12 }}>{m?.id}</span>} />
              </div>
            )}
          </div>

          {/* Kontakt */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editContact ? 20 : 0 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Kontaktperson
              </h3>
              <button className="btn btn-sm btn-ghost" onClick={() => setEditContact(p => !p)}>
                {editContact ? 'Abbrechen' : 'Bearbeiten'}
              </button>
            </div>
            {editContact ? (
              <form onSubmit={saveContact}>
                <div className="grid-2" style={{ gap: 16 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Name *</label>
                    <input className="form-input" value={contactForm.contactName || ''} onChange={e => setContactForm(p => ({ ...p, contactName: e.target.value }))} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Funktion</label>
                    <select className="form-select" value={contactForm.contactRole || 'administrator'} onChange={e => setContactForm(p => ({ ...p, contactRole: e.target.value }))}>
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">E-Mail *</label>
                    <input className="form-input" type="email" value={contactForm.contactEmail || ''} onChange={e => setContactForm(p => ({ ...p, contactEmail: e.target.value }))} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Telefon</label>
                    <input className="form-input" value={contactForm.contactPhone || ''} onChange={e => setContactForm(p => ({ ...p, contactPhone: e.target.value }))} placeholder="+41 ..." />
                  </div>
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" type="submit" disabled={saving === 'contact'}>
                    {saving === 'contact' ? 'Speichern...' : 'Speichern'}
                  </button>
                  <button className="btn btn-ghost" type="button" onClick={() => setEditContact(false)}>Abbrechen</button>
                </div>
              </form>
            ) : (
              <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                <InfoRow label="Name" value={m?.contact_name} />
                <InfoRow label="Funktion" value={ROLES.find(r => r.value === m?.contact_role)?.label || m?.contact_role} />
                <InfoRow label="E-Mail" value={m?.contact_email} />
                <InfoRow label="Telefon" value={m?.contact_phone || '—'} />
              </div>
            )}
          </div>

          {/* Login & Passwort */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editLogin ? 20 : 0 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Zugangsdaten
              </h3>
              <button className="btn btn-sm btn-ghost" onClick={() => setEditLogin(p => !p)}>
                {editLogin ? 'Abbrechen' : 'Passwort zurücksetzen'}
              </button>
            </div>
            {!editLogin && (
              <div style={{ marginTop: 16 }}>
                <InfoRow label="Login-E-Mail" value={m?.login_email} />
                {m?.temp_password && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: '#FEF3E8', borderRadius: 8, fontSize: 13, color: '#B8650C' }}>
                    Temporäres Passwort aktiv — Moschee muss Passwort beim nächsten Login ändern
                  </div>
                )}
              </div>
            )}
            {editLogin && (
              <form onSubmit={savePassword}>
                <div className="grid-2" style={{ gap: 16 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Neues Passwort *</label>
                    <input
                      className="form-input" type="password"
                      value={loginForm.newPassword}
                      onChange={e => setLoginForm(p => ({ ...p, newPassword: e.target.value }))}
                      minLength={8} required placeholder="Mindestens 8 Zeichen"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Passwort wiederholen *</label>
                    <input
                      className="form-input" type="password"
                      value={loginForm.confirmPassword}
                      onChange={e => setLoginForm(p => ({ ...p, confirmPassword: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                  Das Passwort wird als temporär markiert. Die Moschee muss es beim nächsten Login ändern.
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" type="submit" disabled={saving === 'login'}>
                    {saving === 'login' ? 'Speichern...' : 'Passwort setzen'}
                  </button>
                  <button className="btn btn-ghost" type="button" onClick={() => setEditLogin(false)}>Abbrechen</button>
                </div>
              </form>
            )}
          </div>

          {/* Plan */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Plan & Abonnement
              </h3>
              <button className="btn btn-sm btn-ghost" onClick={() => setEditPlan(p => !p)}>
                {editPlan ? 'Abbrechen' : 'Ändern'}
              </button>
            </div>
            {!editPlan ? (
              <div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <span style={{ fontSize: 20, fontWeight: 700 }}>{m?.plan || 'Free'}</span>
                  {m?.is_trial && <span className="badge badge-orange">Testphase</span>}
                  {m?.status === 'active' && <span className="badge badge-green">Aktiv</span>}
                  {m?.status === 'expired' && <span className="badge badge-red">Abgelaufen</span>}
                </div>
                {m?.expires_at && (
                  <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 6 }}>
                    Gültig bis: <strong>{new Date(m.expires_at).toLocaleDateString('de-CH')}</strong>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                  {[
                    { key: 'free', label: 'Kostenlos', color: '#666', bg: '#F0EFED' },
                    { key: 'standard', label: 'Standard', color: '#1A56DB', bg: '#E8F0FE' },
                    { key: 'premium', label: 'Premium', color: '#8B6A1C', bg: '#FBF5E6' },
                  ].map(p => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPlanForm(prev => ({ ...prev, plan: p.key }))}
                      style={{
                        padding: '12px', borderRadius: 10,
                        border: `2px solid ${planForm.plan === p.key ? p.color : 'var(--border)'}`,
                        background: planForm.plan === p.key ? p.bg : 'var(--surface)',
                        cursor: 'pointer', fontWeight: 700, fontSize: 14, color: planForm.plan === p.key ? p.color : 'var(--text)',
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
                  <div>
                    <label className="form-label">Laufzeit (Monate)</label>
                    <select className="form-select" style={{ width: 140 }} value={planForm.months} onChange={e => setPlanForm(p => ({ ...p, months: Number(e.target.value) }))}>
                      {[1,2,3,6,12].map(n => <option key={n} value={n}>{n} Monat{n > 1 ? 'e' : ''}</option>)}
                    </select>
                  </div>
                  {planForm.plan !== 'free' && (
                    <div>
                      <label className="form-label">Betrag (CHF) — optional</label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="z.B. 49.00"
                        value={planForm.amount}
                        onChange={e => setPlanForm(p => ({ ...p, amount: e.target.value }))}
                        style={{ width: 140 }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                        Rechnung + E-Mail werden automatisch erstellt
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" onClick={savePlan} disabled={saving === 'plan'}>
                    {saving === 'plan' ? 'Speichern...' : 'Plan aktualisieren'}
                  </button>
                  <button className="btn btn-ghost" onClick={() => setEditPlan(false)}>Abbrechen</button>
                </div>
              </div>
            )}
          </div>

          {/* Konto-Status */}
          <div className="card">
            <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
              Konto-Verwaltung
            </h3>

            {/* Direct status change */}
            <div style={{ marginBottom: 20 }}>
              <label className="form-label">Status direkt setzen</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(STATUS_LABELS).map(([val, label]) => {
                  const colors = {
                    active:       { bg: '#EAF2EE', color: '#3D6B57', border: '#5B8A75' },
                    blocked:      { bg: '#FDECEA', color: '#D94F3D', border: '#D94F3D' },
                    trial_active: { bg: '#E8F0FE', color: '#1A56DB', border: '#1A56DB' },
                    pending:      { bg: '#FBF5E6', color: '#8B6A1C', border: '#C9A84C' },
                    expired:      { bg: '#F0EFED', color: '#666',    border: '#999' },
                    trial_expired:{ bg: '#F0EFED', color: '#666',    border: '#999' },
                    info_required:{ bg: '#F5EBE5', color: '#C17B5C', border: '#C17B5C' },
                  };
                  const c = colors[val] || { bg: '#F0EFED', color: '#666', border: '#999' };
                  const isActive = m?.status === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      disabled={isActive || !!saving}
                      onClick={() => changeStatus(val)}
                      style={{
                        padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        border: `2px solid ${isActive ? c.border : 'var(--border)'}`,
                        background: isActive ? c.bg : 'var(--surface)',
                        color: isActive ? c.color : 'var(--text3)',
                        cursor: isActive ? 'default' : 'pointer',
                        opacity: !!saving && !isActive ? 0.5 : 1,
                      }}
                    >
                      {isActive ? '● ' : ''}{label}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                Aktuell: <strong>{STATUS_LABELS[m?.status] || m?.status}</strong>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              {m?.status !== 'blocked' ? (
                <button className="btn btn-danger btn-sm" onClick={() => blockUnblock(true)} disabled={!!saving}>
                  Konto sperren
                </button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={() => changeStatus('active')} disabled={!!saving}>
                  Konto entsperren
                </button>
              )}
              {m?.status === 'pending' && (
                <button className="btn btn-primary btn-sm" onClick={() => changeStatus('active')} disabled={!!saving}>
                  Sofort genehmigen
                </button>
              )}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text3)' }}>
              Registriert am: {new Date(m?.created_at).toLocaleDateString('de-CH')}
            </div>
          </div>

          {/* Empfehlungen / Referrals */}
          {(data?.referrer || data?.referred?.length > 0) && (
            <div className="card">
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                Empfehlungen
              </h3>
              {data.referrer && (
                <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--primary-lt)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>Empfohlen von:</div>
                  <div style={{ fontWeight: 600 }}>
                    <a href={`/superadmin/mosques/${data.referrer.id}`} style={{ color: 'var(--primary)' }}>
                      {data.referrer.name}
                    </a>
                    <span style={{ color: 'var(--text3)', fontSize: 12, marginLeft: 8 }}>{data.referrer.city}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, background: 'var(--surface)', padding: '2px 6px', borderRadius: 4, marginLeft: 8, color: 'var(--text2)' }}>
                      {data.referrer.referral_code}
                    </span>
                  </div>
                </div>
              )}
              {data.referred?.length > 0 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
                    {data.referred.length} Moschee{data.referred.length !== 1 ? 'n' : ''} empfohlen:
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                    {data.referred.map((r, i) => (
                      <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i < data.referred.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ flex: 1 }}>
                          <a href={`/superadmin/mosques/${r.id}`} style={{ fontWeight: 600, fontSize: 14, color: 'var(--primary)' }}>{r.name}</a>
                          <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8 }}>{r.city}</span>
                        </div>
                        <span style={{ fontSize: 11, background: '#ECFDF5', color: '#065F46', padding: '3px 8px', borderRadius: 100, fontWeight: 600 }}>{r.plan || 'free'}</span>
                        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{new Date(r.created_at).toLocaleDateString('de-CH')}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Rechnungshistorie */}
          {data?.invoices?.length > 0 && (
            <div className="card">
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                Rechnungshistorie
              </h3>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Nr.</th><th>Plan</th><th>Betrag</th><th>Status</th><th>Datum</th><th></th></tr>
                  </thead>
                  <tbody>
                    {data.invoices.map(inv => (
                      <tr key={inv.id}>
                        <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{inv.invoice_number}</td>
                        <td>{inv.plan}</td>
                        <td>CHF {parseFloat(inv.amount).toFixed(2)}</td>
                        <td>
                          <span className={`badge ${inv.status === 'paid' ? 'badge-green' : 'badge-orange'}`}>
                            {inv.status === 'paid' ? 'Bezahlt' : 'Ausstehend'}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text3)', fontSize: 13 }}>
                          {new Date(inv.created_at).toLocaleDateString('de-CH')}
                        </td>
                        <td>
                          {inv.pdf_path && (
                            <a href={`/api/superadmin/invoices/${inv.id}/pdf`} className="btn btn-sm btn-ghost" target="_blank" rel="noreferrer">
                              PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daten-Export & Gefahrenzone */}
          <div className="card" style={{ borderLeft: '4px solid #dc2626' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Daten-Export &amp; Gefahrenzone
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
              Alle Daten dieser Moschee als JSON exportieren, oder die Moschee mit sämtlichen Daten unwiderruflich löschen.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={doExport}>Daten exportieren (JSON)</button>
              <button className="btn btn-danger btn-sm" onClick={doDelete} disabled={saving === 'delete'}>
                {saving === 'delete' ? 'Wird gelöscht…' : 'Moschee löschen'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ padding: '6px 0' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--text)', marginTop: 2 }}>{value}</div>
    </div>
  );
}
